import { useState } from "react";
import { PieChart } from "lucide-react";
import BarChart from "./BarChart";
import StackedChart from "./StackedChart";
import DonutChart from "./DonutChart";
import type { CategoryStat } from "../../types";

interface CategoryBreakdownProps {
  categories: CategoryStat[];
  total: number;
}

export default function CategoryBreakdown({
  categories,
  total,
}: CategoryBreakdownProps) {
  const [activeTab, setActiveTab] = useState<"bar" | "stack" | "donut">("bar");

  return (
    <div className="rounded-[14px] px-5 py-5 sm:px-6 bg-card border-[0.5px] border-border shadow-card">
      {/* Header */}
      <div className="flex flex-col items-start gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between sm:mb-3.5">
        <h2 className="flex items-center gap-2 text-[15px] font-medium m-0 text-foreground">
          <PieChart className="w-4 h-4 text-[var(--text-secondary)]" />
          Where it went
        </h2>
        {/* Tab switcher */}
        <div
          className="inline-flex rounded-full p-0.5 gap-0.5 bg-[var(--surface-muted)]"
          role="tablist"
        >
          {(["bar", "stack", "donut"] as const).map((v) => (
            <button
              key={v}
              role="tab"
              aria-selected={activeTab === v}
              onClick={() => setActiveTab(v)}
              className={`text-[11px] font-medium px-3 py-1.5 rounded-full transition-colors capitalize ${
                activeTab === v
                  ? "bg-card text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                  : "bg-transparent text-[var(--text-secondary)]"
              }`}
            >
              {v === "stack"
                ? "Stacked"
                : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Chart area */}
      {categories.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[180px] gap-2">
          <PieChart className="w-8 h-8 text-[var(--text-secondary)] opacity-30" />
          <p className="text-sm text-[var(--text-secondary)] opacity-60">
            No expenses this month
          </p>
        </div>
      ) : (
        <>
          {activeTab === "bar" && <BarChart categories={categories} />}
          {activeTab === "stack" && (
            <StackedChart categories={categories} total={total} />
          )}
          {activeTab === "donut" && <DonutChart categories={categories} />}
        </>
      )}
    </div>
  );
}
